import React, { useState } from 'react';
import { SearchIcon, FilterIcon, EyeIcon, MinusSquareIcon, CheckCircleIcon, AlertCircleIcon, CalendarIcon, UserIcon, DollarSignIcon, FileTextIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
export function CreditNoteList({
  creditNotes,
  setCurrentView,
  setSelectedCreditNote,
  locale = 'en-US'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [applicationFilter, setApplicationFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  // Filter credit notes based on search term and filters
  const filteredCreditNotes = creditNotes.filter(creditNote => {
    // Search term filter
    const matchesSearch = creditNote.creditNoteNumber.toLowerCase().includes(searchTerm.toLowerCase()) || creditNote.vendor.toLowerCase().includes(searchTerm.toLowerCase());
    // Status filter
    const matchesStatus = statusFilter === 'all' || creditNote.status === statusFilter;
    // Application filter
    const matchesApplication = applicationFilter === 'all' || applicationFilter === 'applied' && creditNote.unappliedAmount === 0 || applicationFilter === 'unapplied' && creditNote.unappliedAmount > 0 && creditNote.appliedAmount === 0 || applicationFilter === 'partial' && creditNote.unappliedAmount > 0 && creditNote.appliedAmount > 0;
    // Date range filter
    const creditNoteDate = new Date(creditNote.creditNoteDate);
    const fromDate = dateRange.from ? new Date(dateRange.from) : null;
    const toDate = dateRange.to ? new Date(dateRange.to) : null;
    const matchesDateRange = (!fromDate || creditNoteDate >= fromDate) && (!toDate || creditNoteDate <= toDate);
    return matchesSearch && matchesStatus && matchesApplication && matchesDateRange;
  });
  // Calculate pagination
  const totalCreditNotes = filteredCreditNotes.length;
  const totalPages = Math.ceil(totalCreditNotes / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCreditNotes);
  const currentCreditNotes = filteredCreditNotes.slice(startIndex, endIndex);
  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setApplicationFilter('all');
    setDateRange({
      from: '',
      to: ''
    });
    setCurrentPage(1);
  };
  return <div data-id="element-3569">
      <div className="mb-8 flex justify-between items-center" data-id="element-3570">
        <div data-id="element-3571">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3572">Credit Notes</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3573">
            View and manage vendor credit notes
          </p>
        </div>
        <button onClick={() => setCurrentView('credit-note-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3574">
          <MinusSquareIcon className="h-4 w-4 mr-1" data-id="element-3575" />
          New Credit Note
        </button>
      </div>
      {/* Search and Filters */}
      <div className="mb-6 bg-white p-4 shadow rounded-lg" data-id="element-3576">
        <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-4" data-id="element-3577">
          <div className="relative flex-grow max-w-md" data-id="element-3578">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3579">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3580" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Search credit notes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3581" />
          </div>
          <div className="flex space-x-2" data-id="element-3582">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3583">
              <FilterIcon className="h-4 w-4 mr-1" data-id="element-3584" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button onClick={resetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3585">
              Clear Filters
            </button>
          </div>
        </div>
        {/* Expanded Filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-3586">
            <div data-id="element-3587">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3588">
                <div className="flex items-center" data-id="element-3589">
                  <CheckCircleIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3590" />
                  Status
                </div>
              </label>
              <select id="status-filter" value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-3591">
                <option value="all" data-id="element-3592">All Statuses</option>
                <option value="Draft" data-id="element-3593">Draft</option>
                <option value="Pending" data-id="element-3594">Pending</option>
                <option value="Approved" data-id="element-3595">Approved</option>
                <option value="Rejected" data-id="element-3596">Rejected</option>
                <option value="Posted" data-id="element-3597">Posted</option>
              </select>
            </div>
            <div data-id="element-3598">
              <label htmlFor="application-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3599">
                <div className="flex items-center" data-id="element-3600">
                  <DollarSignIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3601" />
                  Application Status
                </div>
              </label>
              <select id="application-filter" value={applicationFilter} onChange={e => {
            setApplicationFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-3602">
                <option value="all" data-id="element-3603">All Applications</option>
                <option value="applied" data-id="element-3604">Fully Applied</option>
                <option value="partial" data-id="element-3605">Partially Applied</option>
                <option value="unapplied" data-id="element-3606">Unapplied</option>
              </select>
            </div>
            <div data-id="element-3607">
              <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3608">
                <div className="flex items-center" data-id="element-3609">
                  <CalendarIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3610" />
                  Date From
                </div>
              </label>
              <input type="date" id="date-from" value={dateRange.from} onChange={e => {
            setDateRange({
              ...dateRange,
              from: e.target.value
            });
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-3611" />
            </div>
            <div data-id="element-3612">
              <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3613">
                <div className="flex items-center" data-id="element-3614">
                  <CalendarIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3615" />
                  Date To
                </div>
              </label>
              <input type="date" id="date-to" value={dateRange.to} onChange={e => {
            setDateRange({
              ...dateRange,
              to: e.target.value
            });
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-3616" />
            </div>
          </div>}
      </div>
      {/* Credit Notes Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-3617">
        {currentCreditNotes.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-3618">
            No credit notes found matching your search criteria.
          </div> : <div className="overflow-x-auto" data-id="element-3619">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3620">
              <thead className="bg-gray-50" data-id="element-3621">
                <tr data-id="element-3622">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3623">
                    <div className="flex items-center" data-id="element-3624">
                      Credit Note #
                      <div className="h-4 w-4 ml-1 text-gray-400" data-id="element-3625" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3626">
                    <div className="flex items-center" data-id="element-3627">
                      Vendor
                      <div className="h-4 w-4 ml-1 text-gray-400" data-id="element-3628" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3629">
                    <div className="flex items-center" data-id="element-3630">
                      Date
                      <div className="h-4 w-4 ml-1 text-gray-400" data-id="element-3631" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3632">
                    <div className="flex items-center" data-id="element-3633">
                      Amount
                      <div className="h-4 w-4 ml-1 text-gray-400" data-id="element-3634" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3635">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3636">
                    Applied
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3637">
                    Invoices
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3638">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3639">
                {currentCreditNotes.map(creditNote => <tr key={creditNote.id} className="hover:bg-gray-50" data-id="element-3640">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-3641">
                      {creditNote.creditNoteNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3642">
                      {creditNote.vendor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3643">
                      {new Date(creditNote.creditNoteDate).toLocaleDateString(locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3644">
                      {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3645">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${creditNote.status === 'Approved' ? 'bg-green-100 text-green-800' : creditNote.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : creditNote.status === 'Draft' ? 'bg-gray-100 text-gray-800' : creditNote.status === 'Posted' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`} data-id="element-3646">
                        {creditNote.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3647">
                      <div className="flex items-center" data-id="element-3648">
                        {creditNote.appliedAmount > 0 ? creditNote.unappliedAmount > 0 ? <AlertCircleIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-3649" /> : <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3650" /> : <AlertCircleIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3651" />}
                        <span className="text-sm text-gray-500" data-id="element-3652">
                          {formatCurrency(creditNote.appliedAmount || 0, creditNote.currency, locale)}
                          {creditNote.unappliedAmount > 0 && creditNote.appliedAmount > 0 && <span className="text-xs text-yellow-600 ml-1" data-id="element-3653">
                                (Partial)
                              </span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3654">
                      {creditNote.linkedInvoices && creditNote.linkedInvoices.length > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3655">
                          {creditNote.linkedInvoices.length}
                        </span> : <span className="text-gray-400" data-id="element-3656">None</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3657">
                      <button onClick={() => {
                  setSelectedCreditNote(creditNote);
                  setCurrentView('credit-note-details');
                }} className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3658">
                        <EyeIcon className="h-4 w-4 mr-1" data-id="element-3659" />
                        View Details
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
        {/* Pagination */}
        {totalPages > 1 && <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-3660">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-3661">
              <div data-id="element-3662">
                <p className="text-sm text-gray-700" data-id="element-3663">
                  Showing <span className="font-medium" data-id="element-3664">{startIndex + 1}</span>{' '}
                  to <span className="font-medium" data-id="element-3665">{endIndex}</span> of{' '}
                  <span className="font-medium" data-id="element-3666">{totalCreditNotes}</span> credit
                  notes
                </p>
              </div>
              <div data-id="element-3667">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-3668">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-3669">
                    <span className="sr-only" data-id="element-3670">Previous</span>
                    &laquo;
                  </button>
                  {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`relative inline-flex items-center px-4 py-2 border ${page === currentPage ? 'bg-indigo-50 border-indigo-500 text-indigo-600 z-10' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-3671">
                      {page}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-3672">
                    <span className="sr-only" data-id="element-3673">Next</span>
                    &raquo;
                  </button>
                </nav>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}